import { useQueryClient } from '@tanstack/react-query';
import { CalendarClock, Check, FileText, MessageSquareWarning, Upload } from 'lucide-react';
import { useRef, useState, type ReactNode } from 'react';
import {
  DOCUMENT_REJECTION_MIN,
  JOINING_DOCUMENT_STATE_LABELS,
  type JoiningDocumentState,
  type JoiningDocumentView,
  type JoiningDocumentsView,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { Chip } from '../../../components/Chip';
import { DatePicker } from '../../../components/DatePicker';
import { Field } from '../../../components/Field';
import { Textarea } from '../../../components/Input';
import { Popover, usePopover } from '../../../components/Popover';
import { JOINING_DOCUMENT_TONES } from '../../../components/status-tones';
import { ToneChip, toneClass } from '../../../components/Tone';
import { errorMessage } from '../../../lib/api-client';
import { formatIsoDate, formatRelative } from '../../../lib/format';
import { useToasts } from '../../../stores/toasts';
import { documentFileUrl, HIRES_KEY, hiresApi } from '../api/hires-api';
import styles from './Hires.module.css';

/** Which control is on its way — feedback stays on that row (CLAUDE.md §5a rule 4). */
type Pending = { readonly key: string; readonly action: 'verify' | 'reject' | 'upload' } | null;

/**
 * F13.12 and §L11 — the job's list of joining documents, each with its newest file. Opening a
 * file marks it under review; Verify is one click; Reject asks why, next to its button, and
 * the candidate is emailed to upload again. Act first, confirm after: the state changes on
 * screen at once and rolls back with the reason if the server refuses.
 */
export function JoiningDocumentsPanel({ view, write }: { readonly view: JoiningDocumentsView; readonly write: (next: JoiningDocumentsView) => void }): ReactNode {
  const queryClient = useQueryClient();
  const toasts = useToasts();
  const [pending, setPending] = useState<Pending>(null);
  const required = view.documents.filter((line) => line.isRequired);
  const verified = required.filter((line) => line.state === 'verified').length;

  async function act(line: JoiningDocumentView, action: 'verify' | 'reject' | 'upload', optimistic: JoiningDocumentState | null, call: () => Promise<JoiningDocumentsView>, failure: string): Promise<void> {
    setPending({ key: line.key, action });
    if (optimistic) write({ ...view, documents: view.documents.map((row) => (row.key === line.key ? { ...row, state: optimistic } : row)) });
    try {
      write(await call());
      await queryClient.invalidateQueries({ queryKey: HIRES_KEY });
    } catch (error) {
      write(view);
      toasts.show({ tone: 'danger', message: failure, detail: errorMessage(error) });
    } finally {
      setPending(null);
    }
  }

  return (
    <Card title="Joining documents" meta={required.length > 0 ? `${verified} of ${required.length} required verified` : 'Nothing is required for this job'}>
      {view.documents.length === 0 ? (
        <p className={styles.quiet}>This job asks for no documents. Its list is in the job's offer settings.</p>
      ) : (
        <ul className={styles.documents}>
          {view.documents.map((line) => (
            <DocumentRow
              key={line.key}
              line={line}
              canReview={view.can.review}
              canUpload={view.can.upload}
              pending={pending?.key === line.key ? pending.action : null}
              onVerify={(file) => void act(line, 'verify', 'verified', () => hiresApi.review(file.id, { decision: 'verified', version: file.version }), `${line.label} wasn't verified`)}
              onReject={(file, reason) => void act(line, 'reject', 'rejected', () => hiresApi.review(file.id, { decision: 'rejected', version: file.version, reason }), `${line.label} wasn't rejected`)}
              onUpload={(upload, validUntil) => void act(line, 'upload', null, () => hiresApi.upload(view.offerId, line.key, upload, validUntil), `${line.label} wasn't uploaded`)}
              onOpened={() => void queryClient.invalidateQueries({ queryKey: HIRES_KEY })}
            />
          ))}
        </ul>
      )}
    </Card>
  );
}

function DocumentRow({
  line,
  canReview,
  canUpload,
  pending,
  onVerify,
  onReject,
  onUpload,
  onOpened,
}: {
  readonly line: JoiningDocumentView;
  readonly canReview: boolean;
  readonly canUpload: boolean;
  readonly pending: 'verify' | 'reject' | 'upload' | null;
  readonly onVerify: (file: NonNullable<JoiningDocumentView['file']>) => void;
  readonly onReject: (file: NonNullable<JoiningDocumentView['file']>, reason: string) => void;
  readonly onUpload: (file: File, validUntil: string | null) => void;
  readonly onOpened: () => void;
}): ReactNode {
  const file = line.file;
  const isDecidable = canReview && file !== null && (line.state === 'uploaded' || line.state === 'under_review');
  return (
    <li className={`${toneClass} ${styles.document}`} data-tone={JOINING_DOCUMENT_TONES[line.state]}>
      <div className={styles.documentHead}>
        <span className={styles.documentName}>{line.label}</span>
        <Chip tone={line.isRequired ? 'attention' : 'neutral'} plain>
          {line.isRequired ? 'Required' : 'Optional'}
        </Chip>
        <ToneChip tone={JOINING_DOCUMENT_TONES[line.state]}>{JOINING_DOCUMENT_STATE_LABELS[line.state]}</ToneChip>
      </div>
      {file && (
        <div className={styles.documentFile}>
          {canReview ? (
            <a className={styles.fileLink} href={documentFileUrl(file.id)} target="_blank" rel="noreferrer" onClick={onOpened}>
              <FileText size={14} aria-hidden /> {file.fileName}
            </a>
          ) : (
            <span className={styles.meta}>{file.fileName}</span>
          )}
          <span className={styles.meta}>
            {file.uploadedBy === 'candidate' ? 'From the candidate' : 'Uploaded by staff'} · {formatRelative(file.uploadedAt)}
            {file.validUntil ? ` · valid until ${formatIsoDate(file.validUntil)}` : ''}
            {line.earlierCount > 0 ? ` · ${line.earlierCount} earlier ${line.earlierCount === 1 ? 'file' : 'files'} kept` : ''}
          </span>
          {file.rejectionReason && <p className={styles.quote}>“{file.rejectionReason}”</p>}
          {file.decidedByName && line.state === 'verified' && <span className={styles.meta}>Verified by {file.decidedByName}</span>}
        </div>
      )}
      {(isDecidable || canUpload) && (
        <div className={styles.documentActions}>
          {isDecidable && file && (
            <>
              <Button variant="solid" size="sm" icon={Check} isPending={pending === 'verify'} onClick={() => onVerify(file)}>
                Verify
              </Button>
              <RejectButton label={line.label} isPending={pending === 'reject'} onReject={(reason) => onReject(file, reason)} />
            </>
          )}
          {canUpload && <UploadButton hasFile={file !== null} isPending={pending === 'upload'} onUpload={onUpload} />}
        </div>
      )}
    </li>
  );
}

/** §L11: a rejection needs a reason; asked next to its button (CLAUDE.md §5). */
function RejectButton({ label, isPending, onReject }: { readonly label: string; readonly isPending: boolean; readonly onReject: (reason: string) => void }): ReactNode {
  const { isOpen, anchor, triggerRef, toggle, close } = usePopover();
  const [reason, setReason] = useState('');
  const isReady = reason.trim().length >= DOCUMENT_REJECTION_MIN;
  return (
    <>
      <Button ref={triggerRef} variant="outline" size="sm" icon={MessageSquareWarning} isPending={isPending} onClick={toggle} aria-expanded={isOpen}>
        Reject
      </Button>
      <Popover anchor={anchor} onClose={close} label={`Reject ${label}`} width={340}>
        <div className={styles.popoverBody}>
          <Field label="What's wrong with it?" isRequired hint="The candidate is emailed this and asked to upload it again.">
            {({ inputId }) => <Textarea id={inputId} rows={3} maxLength={500} value={reason} placeholder="The photo is blurred — the numbers can't be read." onChange={(event) => setReason(event.target.value)} />}
          </Field>
          <Button
            variant="solid"
            block
            disabled={!isReady}
            onClick={() => {
              close();
              onReject(reason.trim());
              setReason('');
            }}
          >
            Reject and ask again
          </Button>
        </div>
      </Popover>
    </>
  );
}

/** Upload straight away; "Expires?" first asks the date for a permit or certificate (§L11 `expired`). */
function UploadButton({ hasFile, isPending, onUpload }: { readonly hasFile: boolean; readonly isPending: boolean; readonly onUpload: (file: File, validUntil: string | null) => void }): ReactNode {
  const input = useRef<HTMLInputElement | null>(null);
  const { isOpen, anchor, triggerRef, toggle, close } = usePopover();
  const [validUntil, setValidUntil] = useState('');
  const chosenDate = useRef<string | null>(null);
  return (
    <>
      <input
        ref={input}
        className={styles.hiddenFile}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        aria-label="Choose the document"
        onChange={(event) => {
          const chosen = event.target.files?.[0];
          event.target.value = '';
          if (chosen) onUpload(chosen, chosenDate.current);
          chosenDate.current = null;
        }}
      />
      <Button variant="outline" size="sm" icon={Upload} isPending={isPending} onClick={() => input.current?.click()}>
        {hasFile ? 'Upload a new one' : 'Upload'}
      </Button>
      <Button ref={triggerRef} variant="ghost" size="sm" icon={CalendarClock} onClick={toggle} aria-expanded={isOpen}>
        Expires?
      </Button>
      <Popover anchor={anchor} onClose={close} label="Upload a document that expires" width={320}>
        <div className={styles.popoverBody}>
          <DatePicker label="Valid until" value={validUntil} onChange={setValidUntil} />
          <p className={styles.quiet}>After this date it counts as expired, and joining waits for a current one.</p>
          <Button
            variant="solid"
            block
            disabled={validUntil === ''}
            onClick={() => {
              chosenDate.current = validUntil;
              close();
              input.current?.click();
            }}
          >
            Choose the file
          </Button>
        </div>
      </Popover>
    </>
  );
}
